import { AppState } from "../reducers";
import { createSelector } from "reselect";
import { LibraryCard, CardType, CardPropertyType } from "../../types/LibraryCard";
import { parseISO, getYear, getMonth, isAfter, addMonths, differenceInCalendarMonths } from "date-fns";

export interface CardWithDate {
	startDate: Date;
	endDate?: Date;
	card: LibraryCard;
	type: CardType;
}

export interface CardsByMonths {
	[month: number]: CardWithDate[];
}

export interface CardsByYears {
	[year: number]: CardsByMonths;
}

export const getState = (state: AppState) => state.calendarBooks;

const sortByDate = (a: LibraryCard, b: LibraryCard) => {
	const aDateBegin = a.properties[CardPropertyType.DateBegin] ? parseISO(a.properties[CardPropertyType.DateBegin]) : null;
	const bDateBegin = b.properties[CardPropertyType.DateBegin] ? parseISO(b.properties[CardPropertyType.DateBegin]) : null;

	if (aDateBegin && bDateBegin) {
		return isAfter(aDateBegin, bDateBegin) ? -1 : 1;
	}

	// Книга без дат идет первой
	if (aDateBegin && !bDateBegin) {
		if (b.history.length > 0 && b.history[0].startDate) {
			return isAfter(aDateBegin, parseISO(b.history[0].startDate as any)) ? -1 : 1;
		}

		return 1;
	}

	if (bDateBegin && !aDateBegin) {
		if (a.history.length > 0 && a.history[0].startDate) {
			return isAfter(parseISO(a.history[0].startDate as any), bDateBegin) ? -1 : 1;
		}

		return -1;
	}
}

export const getCardsArray = createSelector([getState], (state) => {
	const array: LibraryCard[] = [];

	for (const book in state) {
		if (state.hasOwnProperty(book) && state[book]) {
			array.push(state[book]);
		}
	}

	return array.sort(sortByDate);
});

export const getAlreadyReadBooks = createSelector([getCardsArray], (cards) => {
	return cards.filter(card => card.cardType === CardType.AlreadyRead);
});

export const getReadNowBooks = createSelector([getCardsArray], (cards) => {
	return cards.filter(card => card.cardType === CardType.ReadNow);
});

export const getWantReadBooks = createSelector([getCardsArray], (cards) => {
	return cards.filter(card => card.cardType === CardType.WantRead);
});

/**
 * @deprecated
 */
export const getCardsByDate = createSelector([getState], (cards) => {
	let cardsByYears: CardsByYears = {};
	const addedBooksMap = new Map<string, string>(); // чтобы не добавлять в один месяц несколько раз одну книгу

	for (const book in cards) {
		if (
			cards.hasOwnProperty(book) && cards[book] &&
			(
				cards[book].properties[CardPropertyType.DateBegin] ||
				(
					cards[book].history && cards[book].history.length > 0
				)
			)
		) {
			const card = cards[book];

			if (card.properties[CardPropertyType.DateBegin]) {
				const parsedDate = parseISO(card.properties[CardPropertyType.DateBegin]),
					year = getYear(parsedDate),
					month = getMonth(parsedDate),
					checkKey = `${year}-${month}-${card.eId}`;
				
				if (!addedBooksMap.has(checkKey)) {
					const startDate = parsedDate;
					const endDate = card.properties[CardPropertyType.DateFinish] && parseISO(card.properties[CardPropertyType.DateBegin]);

					cardsByYears = {
						...cardsByYears,
						[year]: {
							...cardsByYears[year],
							[month]: cardsByYears[year] && cardsByYears[year][month] ? [
								...cardsByYears[year][month],
								{
									card,
									startDate,
									endDate,
									type: card.cardType
								}
							] : [
									{
										card,
										startDate,
										endDate,
										type: card.cardType
									}
								]
						}
					}

					addedBooksMap.set(checkKey, card.eId.toString());
				}
			}

			if (card.history && card.history.length > 0) {
				card.history.forEach(history => {
					const parsedDate = parseISO(history.startDate as any),
						year = getYear(parsedDate),
						month = getMonth(parsedDate),
						checkKey = `${year}-${month}-${card.eId}`;
					
					if (!addedBooksMap.has(checkKey)) {
						const startDate = parsedDate;
						const endDate = history.finishDate && parseISO(history.finishDate as any);

						cardsByYears = {
							...cardsByYears,
							[year]: {
								...cardsByYears[year],
								[month]: cardsByYears[year] && cardsByYears[year][month] ? [
									...cardsByYears[year][month],
									{
										card,
										startDate,
										endDate,
										type: CardType.AlreadyRead
									}
								] : [{
									card,
									startDate,
									endDate,
									type: CardType.AlreadyRead
								}]
							}
						}

						addedBooksMap.set(checkKey, card.eId.toString());
					}
				});
			}
		}
	}

	return cardsByYears;
});

interface DatePeriod {
	start: Date;
	end?: Date;
	type?: CardType;
}

export const getCardsByDateNew = createSelector([getState], (cards) => {
	let cardsByYears: CardsByYears = {};
	const addedBooksMap = new Map<string, string>(); // чтобы не добавлять в один месяц несколько раз одну книгу

	for (const cardId in cards) {
		if (cards.hasOwnProperty(cardId)) {
			const card = cards[cardId];
			const bookDates: DatePeriod[] = [];

			// Получаем все даты из карточки
			if (card.properties[CardPropertyType.DateBegin]) {
				bookDates.push({
					...getDatePeroid(
						card.properties[CardPropertyType.DateBegin],
						card.properties[CardPropertyType.DateFinish]
					),
					type: card.cardType
				});
			}

			if (card.history.length) {
				card.history.forEach(history => {
					bookDates.push({
						...getDatePeroid(
							history.startDate as any,
							history.finishDate as any
						),
						type: CardType.AlreadyRead
					})
				});
			}

			// Начинаем обрабатывать все собранные даты
			bookDates.forEach(peroid => {
				const monthCount = peroid.end ? differenceInCalendarMonths(peroid.end, peroid.start) : 0;
				let currentMonth = peroid.start;

				for (let i = 0; i <= monthCount; i++) {
					// const checkKey = `${currentMonth.getFullYear()}-${currentMonth.getMonth()}-${card.eId}`;

					// if (!addedBooksMap.has(checkKey)) {
						cardsByYears = pushToState(
							cardsByYears,
							card,
							currentMonth,
							peroid.start,
							peroid.end,
							peroid.type
						);

					//	addedBooksMap.set(checkKey, checkKey);
					// }

					currentMonth = addMonths(currentMonth, 1);
				}
			});
		}
	}

	return cardsByYears;
});

const getDatePeroid = (start: string, end?: string): DatePeriod => {
	return {
		start: parseISO(start),
		end: end ? parseISO(end) : null
	}
};

const pushToState = (state: CardsByYears, card: LibraryCard, current: Date, start: Date, end?: Date, type?: CardType): CardsByYears => {
	const year = current.getFullYear(),
		month = current.getMonth();

	return {
		...state,
		[year]: {
			...getStateYearState(state, year),
			[month]: [
				...getStateMonth(state, year, month),
				{
					card,
					startDate: start,
					endDate: end,
					type
				}
			]
		}
	}
};

const getStateYearState = (state: CardsByYears, year: number) => {
	return state[year] || {};
}

const getStateMonth = (state: CardsByYears, year: number, month: number) => {
	return state[year]?.[month] || [];
}